import {
	Button,
	ColorKeys,
	createMarkup,
	Flex,
	Icon,
	IconTypes,
	INNER_MAX_WIDTH,
	isIconType,
	READABLE_LINE_MAX_WIDTH,
	Rte,
} from "@capioweb/ui-components/src"
import styled from "styled-components"
import { QBankImage } from "../../@types/CapioWeb/core"
import {
	BooleanProperty,
	LinkProperty,
	StringProperty,
} from "../../@types/EpiFoundationSpa/Property"
import { imageBlockDataToImageProps, sendImageTextCtaClickLayerPush, urlIsInternal } from "../../utils"

interface CapioImageTextBlockProps {
	backgroundColor: StringProperty
	body: StringProperty
	icon: StringProperty
	imageAlignment: StringProperty
	imageBlock: QBankImage
	textBackgroundColor: BooleanProperty
	leftAligned: BooleanProperty
	cta: LinkProperty
}

const CapioImageTextBlock: React.FC<CapioImageTextBlockProps> = (props) => {
	interface ColorSetProps {
		backgroundColor: ColorKeys
		iconColor: ColorKeys
		textBackgroundColor: ColorKeys
	}
	const srcSetSizes = [{ width: 1000, breakpoint: 2560 }]

	const colorSet = (color: string) => {
		switch (color) {
			case "green33":
				return {
					backgroundColor: "green33",
					iconColor: "green100",
					textBackgroundColor: "green11",
				}

			case "blue33":
				return {
					backgroundColor: "blue33",
					iconColor: "blue66",
					textBackgroundColor: "blue11",
				}

			case "purple33":
				return {
					backgroundColor: "purple33",
					iconColor: "purple100",
					textBackgroundColor: "purple11",
				}

			case "coral33":
				return {
					backgroundColor: "coral33",
					iconColor: "coral66",
					textBackgroundColor: "coral11",
				}
			case "yellow33":
				return {
					backgroundColor: "yellow33",
					iconColor: "yellow100",
					textBackgroundColor: "yellow11",
				}
			default:
				return {
					backgroundColor: "",
					iconColor: "",
				}
		}
	}

	const ColorSet = colorSet(props.backgroundColor?.value) as ColorSetProps

	const IconBox = (icon: string) => {
		const getIcon = (icon: string): IconTypes | undefined => {
			if (isIconType(icon)) {
				return icon as IconTypes
			}
			return undefined
		}

		const StyledIcon = styled.div`
			width: 60%;
			margin: auto;

			&svg {
				width: 100%;
				height: 100%;
			}
		`

		return (
			<Flex w={1} h={1} backgroundColor={ColorSet?.backgroundColor}>
				<StyledIcon>
					<Icon
						icon={getIcon(icon)}
						color={ColorSet.iconColor}
						size="100%"
					></Icon>
				</StyledIcon>
			</Flex>
		)
	}

	const ctaLink = props.cta

	return (
		<Flex as="section" w={1} px={3} pb={3}>
			<Flex
				w={1}
				flexDirection="column"
				maxw={INNER_MAX_WIDTH}
				style={{ margin: "0 auto" }}
			>
				<Flex
					maxw={READABLE_LINE_MAX_WIDTH}
					flexDirection="column"
					mx={props.leftAligned.value ? 0 : "auto"}
					w={1}
				>
					<Flex
						w={1}
						columnGap={props.textBackgroundColor?.value ? 0 : 3}
						rowGap={props.textBackgroundColor?.value ? 0 : 3}
						flexDirection={{
							_:
								props.imageAlignment?.value?.toLowerCase() === "right"
									? "column"
									: "column-reverse",
							m:
								props.imageAlignment?.value?.toLowerCase() === "right"
									? "row"
									: "row-reverse",
						}}
					>
						<Flex
							p={props.textBackgroundColor?.value && { _: 3, m: 9 }}
							w={{
								_: 1,
								m: props.textBackgroundColor?.value
									? "50%"
									: "calc(50% - 12px)",
							}}
							flexDirection="column"
							backgroundColor={ColorSet.textBackgroundColor}
						>
							<Rte>
								<div
									dangerouslySetInnerHTML={createMarkup(props.body?.value)}
								></div>
							</Rte>
							{ctaLink?.href &&
								(!urlIsInternal(ctaLink?.href) ? (
									<Button.Anchor
										href={ctaLink.href}
										target={ctaLink.target}
										title={ctaLink.title}
										mt={1}
										onClick={() => sendImageTextCtaClickLayerPush(ctaLink.href)}
									>
										{ctaLink.text}
									</Button.Anchor>
								) : (
									<Button.Link
										to={ctaLink?.href ?? "#"}
										title={ctaLink?.title}
										mt={1}
										onClick={() => sendImageTextCtaClickLayerPush(ctaLink.href)}
									>
										{ctaLink?.text}
									</Button.Link>
								))
							}
						</Flex>
						<Flex
							w={{
								_: 1,
								m: props.textBackgroundColor?.value
									? "50%"
									: "calc(50% - 12px)",
							}}
							maxh={80}
							minh={25}
							position="relative"
						>
							{props.imageBlock?.media?.value ? (
								<img
									{...imageBlockDataToImageProps(
										props?.imageBlock,
										true,
										true,
										srcSetSizes
									)}
									style={{
										objectFit: "cover",
										position: "absolute",
										width: "100%",
										height: "100%",
									}}
								/>
							) : (
								<>{IconBox(props.icon?.value)}</>
							)}
						</Flex>
					</Flex>
				</Flex>
			</Flex>
		</Flex>
	)
}

export { CapioImageTextBlock }
